import type { GlyphInstance } from './layout';

/**
 * Draws a laid-out glyph somewhere else on the canvas.
 *
 * Techniques that scatter letters around — leaves on a tree, glyph particles, text
 * built from text — need the real outline of a specific letter at an arbitrary
 * position, scale and rotation. The layout already produced flattened contours in
 * canvas space, so this re-anchors them on the glyph's own centre and applies a fresh
 * transform. No font access, no re-flattening.
 */
export function buildGlyphPath(
  glyph: GlyphInstance,
  x: number,
  y: number,
  scale: number,
  rotation: number,
): Path2D {
  const path = new Path2D();
  const cx = glyph.bounds.x + glyph.bounds.width / 2;
  const cy = glyph.bounds.y + glyph.bounds.height / 2;
  const cos = Math.cos(rotation);
  const sin = Math.sin(rotation);

  for (const contour of glyph.contours) {
    const p = contour.points;
    if (p.length < 6) continue;
    for (let i = 0; i < p.length; i += 2) {
      const lx = (p[i] - cx) * scale;
      const ly = (p[i + 1] - cy) * scale;
      const px = x + lx * cos - ly * sin;
      const py = y + lx * sin + ly * cos;
      if (i === 0) path.moveTo(px, py);
      else path.lineTo(px, py);
    }
    path.closePath();
  }
  return path;
}

/** The size a glyph should be drawn at to end up `target` px tall. */
export function scaleForHeight(glyph: GlyphInstance, target: number): number {
  return glyph.bounds.height > 0.01 ? target / glyph.bounds.height : 1;
}

/** Drawable glyphs only — whitespace has no contours and would render nothing. */
export function drawableGlyphs(glyphs: readonly GlyphInstance[]): GlyphInstance[] {
  return glyphs.filter((g) => !g.isWhitespace && g.contours.length > 0);
}
